import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useSubprojectStatusesForBoards } from '../api/subprojects-client/subprojects-client.hooks';
import {
  buildStatusLabelMap,
  createStatusLabelResolver,
  identityStatusLabelResolver,
  mergeStatusLabelMaps,
} from '../helpers/status-labels/status-labels';
import type { StatusLabelMap, StatusLabelResolver } from '../helpers/status-labels/status-labels.type';

type StatusLabelsContextType = {
  labelMap: StatusLabelMap;
  getStatusLabel: StatusLabelResolver;
  isLoading: boolean;
};

const StatusLabelsContext = createContext<StatusLabelsContextType | null>(null);

type StatusLabelsProviderProps = {
  children: ReactNode;
  subprojectIds: (string | null | undefined)[];
};

const EMPTY_LABEL_MAP: StatusLabelMap = new Map();

/**
 * Resolves source-cased status labels for an explicit set of boards, without
 * requiring a surrounding provider.
 *
 * Use this where the consumer already knows its board scope and just needs a
 * resolver (chart metadata, chips, plain helpers). `StatusLabelsProvider` is
 * built on top of it for the cases where the consumer is too deep in the tree
 * to receive a prop.
 *
 * @param {(string | null | undefined)[]} subprojectIds - Boards whose statuses are being rendered; nullish and duplicate entries are ignored
 * @returns {StatusLabelsContextType} The merged label map, a resolver over it, and whether any board is still loading
 */
export function useStatusLabelResolver(subprojectIds: (string | null | undefined)[]): StatusLabelsContextType {
  const { statusesBySubprojectId, isLoading } = useSubprojectStatusesForBoards(subprojectIds);

  const labelMap = useMemo(
    () => mergeStatusLabelMaps(Object.values(statusesBySubprojectId).map(buildStatusLabelMap)),
    [statusesBySubprojectId],
  );

  const getStatusLabel = useMemo(() => createStatusLabelResolver(labelMap), [labelMap]);

  return useMemo(() => ({ labelMap, getStatusLabel, isLoading }), [labelMap, getStatusLabel, isLoading]);
}

/**
 * Provides descendants with source-cased display labels for the statuses of
 * the boards currently in scope.
 *
 * Responsibilities:
 * 1. Fetch status names for every board in `subprojectIds`, tolerating boards
 *    the user cannot see (those simply contribute no labels).
 * 2. Merge the per-board results into one normalized-key lookup so a single
 *    resolver serves surfaces that span multiple boards.
 * 3. Memoize the resolver and context value so consumers re-render only when
 *    the underlying label set actually changes.
 *
 * Wrapping is the only requirement placed on consumers: statuses render in
 * source casing inside the provider and in their raw stored casing outside it.
 * Values passed back to the API are never touched by this context.
 *
 * @param {ReactNode} children - The subtree that renders status labels
 * @param {(string | null | undefined)[]} subprojectIds - Boards whose statuses are being rendered; nullish entries are ignored so callers can pass unresolved scope directly
 */
export function StatusLabelsProvider({ children, subprojectIds }: StatusLabelsProviderProps) {
  const value = useStatusLabelResolver(subprojectIds);

  return <StatusLabelsContext.Provider value={value}>{children}</StatusLabelsContext.Provider>;
}

/**
 * Reads the status-label context.
 *
 * Falls back to an identity resolver outside a provider so a consumer that has
 * not been wrapped keeps rendering raw status values instead of crashing or
 * blanking — the same safe-default posture `useMappedPhases` takes.
 *
 * @returns {StatusLabelsContextType} The active label lookup, or an identity fallback
 */
export function useStatusLabels(): StatusLabelsContextType {
  const ctx = useContext(StatusLabelsContext);
  // Memoized module-level constants keep the fallback object referentially
  // stable so consumer `useMemo`s keyed on it don't invalidate every render.
  const fallback = useMemo<StatusLabelsContextType>(
    () => ({
      labelMap: EMPTY_LABEL_MAP,
      getStatusLabel: identityStatusLabelResolver,
      isLoading: false,
    }),
    [],
  );
  return ctx ?? fallback;
}
